/**
 * Compresses and resizes an image file while maintaining its aspect ratio.
 * Returns the compressed and resized image file, or the original file if an error occurs.
 * @param {File} file - The original image file to be compressed and resized.
 * @param {number} [maxWidth=720] - The maximum width of the resized image.
 * @param {number} [maxHeight=720] - The maximum height of the resized image.
 * @param {number} [quality=0.7] - The quality of the compressed image (0 to 1).
 * @returns {Promise<File>} A promise that resolves to the compressed and resized image file, or the original file if an error occurs.
 */
export const CompressImageFile = (file: File, maxWidth: number = 720, maxHeight: number = 720, quality: number = 0.7): Promise<File> => {
    //const benchid = `CompressImageFile-${file?.name}-${Date.now()}`;

    //console.time(benchid);

    return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) {
            resolve(file);

            return;
        }

        const img = new Image();

        img.onload = function () {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    console.error('Error creating Resize canvas context.');
                    console.timeEnd('Image Processing');

                    resolve(file);

                    return;
                }

                let width = img.width;
                let height = img.height;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                }
                else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                // Compress the image using toBlob
                canvas.toBlob(function (blob) {
                    if (blob) {
                        const compressed = new File([blob], file.name,
                            {
                                type: file.type,
                                lastModified: Date.now()
                            });

                        //console.timeEnd(benchid);

                        resolve(compressed);
                    }
                    else {
                        console.error('Canvas toBlob failed');
                        //console.timeEnd(benchid);

                        resolve(file);
                    }
                }, file.type, quality);
            }
            catch (error) {
                console.error('Error during image processing:', error);
                //console.timeEnd(benchid);

                resolve(file);
            }
        };

        const reader = new FileReader();

        reader.onload = function (e) {
            img.src = e.target?.result as string;
        };

        reader.onerror = function (error) {
            console.error('FileReader error:', error);
            //console.timeEnd(benchid);

            resolve(file);
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Converts a File object to ArrayBuffer
 * @param {File} file - The original image file to be compressed and resized.
 */
export const ConvertToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = (error) => reject(error);

        reader.readAsArrayBuffer(file);
    });
}

/**
 * Checks if a string value (extension) is of type jpg,jpeg or png.
 * @param {string} extension - the file extension.
 */
export const IsImageExtension = (extension: string) => {
    if (!extension) return false;

    const imageExtensions = ['.png', '.jpg', '.jpeg'];

    return imageExtensions.includes(extension?.toLowerCase());
}

export const CompressUtils = { CompressImageFile, ConvertToArrayBuffer, IsImageExtension };